import {
  forwardRef,
  useCallback,
  useRef,
  useMemo,
  createContext,
  useContext,
  useImperativeHandle,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ClipLoader } from 'react-spinners';
import { useClickAway } from 'react-use';
import { useEditor as useTipTapEditor } from '@tiptap/react';
import TTTextStyle from '@tiptap/extension-text-style';
import TTUnderline from '@tiptap/extension-underline';
import TTStrike from '@tiptap/extension-strike';
import TTOrderedList from '@tiptap/extension-ordered-list';
import TTListItem from '@tiptap/extension-list-item';
import TTTextAlign from '@tiptap/extension-text-align';
import TTBulletList from '@tiptap/extension-bullet-list';
import TTItalic from '@tiptap/extension-italic';
import TTBold from '@tiptap/extension-bold';
import TTDocument from '@tiptap/extension-document';
import TTParagraph from '@tiptap/extension-paragraph';
import TTText from '@tiptap/extension-text';
import TTPlaceholder from '@tiptap/extension-placeholder';
import TTHistory from '@tiptap/extension-history';
import TTLink from '@tiptap/extension-link';
import TTHorizontalRule from '@tiptap/extension-horizontal-rule';
import { FontFamily } from './plugins/font-family';
import { FontSize } from './plugins/font-size';
import { Color } from './plugins/color';
import { Highlight } from './plugins/highlight';
import { LineHeight } from './plugins/line-height';
import { grayScale } from 'app/colors';
import Button from 'components/Button';
import { MergeField, MergeFieldList } from './MergeFieldNode';
import { useWYSIWYGAIContext } from './ai-context';
import { renderWith } from './utils/suggestions';
import {
  CODE_BLOCK_LANGUAGES,
  getCodeBlockExtension,
  getOnChange,
} from './codeBlockExtension';
import {
  BottomBarWrapper,
  EditorWrapper,
  FloatingEditorWrapper,
  LoadingContainer,
  ResizeIcon,
  StyledEditorContent,
  StyledResizableBox,
} from './styles';

const EditorContext = createContext({});
export const useEditor = () => useContext(EditorContext).editor;

const ExtendedLink = TTLink.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      'data-script-id': {
        default: null,
      },
    };
  },
});

/**
 * @typedef {{ label: string; relationship: string }} Item
 * @typedef {{ items: Item[]; key: string; label: string }} Category
 * @typedef {{ text: string; options: Category[] | Item[] }} Opts
 *
 * @param {Opts} opts
 * @returns {string[]}
 */
const getUnavailableMergeFields = ({ text, options }) => {
  const result = [];

  if (!options) {
    return result;
  }

  const relationships = options.reduce((acc, maybeCategory) => {
    if (Array.isArray(maybeCategory.items)) {
      for (const opt of maybeCategory.items) {
        acc.add(opt.relationship);
      }
    } else if ('relationship' in maybeCategory) {
      acc.add(maybeCategory.relationship);
    }
    return acc;
  }, new Set());

  const matches = text.matchAll(
    /data-merge-field-relationship="(.*)".{{\s*\1\s*}}/gm
  );

  for (const match of matches) {
    if (!relationships.has(match[1])) {
      result.push(match[1]);
    }
  }

  return result;
};

const EditorContainer = forwardRef(
  (
    {
      floating,
      fitToContent,
      showBorder,
      initialHeight,
      minHeight = 100,
      enableResize,
      hasLoadingState = false,
      isLoading = false,
      children,
      codeBlockColors,
      ...rest
    },
    ref
  ) => {
    return floating ? (
      <FloatingEditorWrapper
        ref={ref}
        fitToContent
        showBorder={showBorder}
        hasLoadingState={hasLoadingState}
        isLoading={isLoading}
        codeBlockColors={codeBlockColors}
        {...rest}
      >
        {children}
      </FloatingEditorWrapper>
    ) : (
      <EditorWrapper
        ref={ref}
        fitToContent={fitToContent}
        enableResize={enableResize}
        initialHeight={initialHeight}
        hasLoadingState={hasLoadingState}
        isLoading={isLoading}
        codeBlockColors={codeBlockColors}
        {...rest}
      >
        {!enableResize || fitToContent ? (
          children
        ) : (
          <StyledResizableBox
            axis="y"
            resizeHandles={['n', 's']}
            height={initialHeight}
            minConstraints={[
              10,
              Math.min(initialHeight || minHeight, minHeight),
            ]}
            handle={
              <ResizeIcon icon="column-resize" color={grayScale.mediumDark} />
            }
          >
            {children}
          </StyledResizableBox>
        )}
      </EditorWrapper>
    );
  }
);

/*
 * hook to allow an optional callback to never
 * change locally but to ref to the latest
 */
const usePropCallback = (maybeCallbackProp) => {
  const ref = useRef(maybeCallbackProp);
  ref.current = maybeCallbackProp;
  // This will never change
  return useCallback((val) => {
    if (ref.current) {
      ref.current(val);
    }
  }, []);
};

export const WYSIWYG = forwardRef(
  (
    {
      onInit,
      onChange,
      onFocus,
      onBlur,
      children,

      mentionConfig = null,

      initialValue = '',
      initialHeight = 200,
      minHeight = 100,

      defaultFontFamily,
      defaultFontSize,
      defaultLineHeight,

      autofocus = false,
      fitToContent = false,
      floating = false,
      showBorder = false,
      zIndex,
      bottomBarNode,

      placeholder,

      enableResize = true,
      enableAI = false,
      enableTextStyles = false,
      enableTextAlign = false,
      enableLists = false,
      enableFontFamily = false,
      enableFontSize = false,
      enableLineHeight = false,
      enableColor = false,
      enableHighlight = false,
      enableLinks = false,
      enableMergeFields = false,
      enableHorizontalRule = false,
      disabled = false,
      mergeFields = null,
      codeBlockLanguage = null,
      disableCodeBlockEscape = false,
    },
    ref
  ) => {
    const containerRef = useRef();
    const { t } = useTranslation();
    const onChangeCB = usePropCallback(
      getOnChange(onChange, codeBlockLanguage && disableCodeBlockEscape)
    );
    const onFocusCB = usePropCallback(onFocus);
    const onBlurCB = usePropCallback(onBlur);
    const onInitCB = usePropCallback(onInit);
    const {
      isLoading: isLoadingAIContent,
      setShowDismissModal,
      cancelRequest,
    } = useWYSIWYGAIContext();
    const editorConfigDeps = [defaultLineHeight, placeholder, disabled];

    const editorConfig = useMemo(() => {
      return {
        onUpdate: onChangeCB,
        onFocus: onFocusCB,
        onBlur: onBlurCB,
        onCreate: onInitCB,
        extensions: [
          // Required defaults
          TTDocument,
          TTText,
          TTParagraph,
          TTTextStyle,
          TTHistory,

          placeholder && TTPlaceholder.configure({ placeholder }),

          enableFontFamily && FontFamily,
          enableFontSize && FontSize,
          enableLineHeight &&
            LineHeight.configure({ defaultValue: defaultLineHeight }),

          enableTextStyles && TTBold,
          enableTextStyles && TTItalic,
          enableTextStyles && TTUnderline,
          enableTextStyles && TTStrike,
          enableTextStyles && enableColor && Color,
          enableTextStyles && enableHighlight && Highlight,

          enableLists && TTListItem,
          enableLists && TTOrderedList,
          enableLists && TTBulletList,
          enableHorizontalRule && TTHorizontalRule,

          enableTextAlign && TTTextAlign.configure({ types: ['paragraph'] }),

          enableLinks &&
            ExtendedLink.configure({
              openOnClick: false,
              HTMLAttributes: {
                target: null, // default is _blank which doesn't let us turn it off in the builder - setting null here fixes that.
              },
            }),

          mentionConfig !== null &&
            mentionConfig.extension.configure(mentionConfig.configure),
          enableMergeFields &&
            MergeField.configure({
              mergeFields,
              unavailableMergeFields: getUnavailableMergeFields({
                text: initialValue,
                options: mergeFields,
              }),
              suggestion: {
                render: renderWith(MergeFieldList, {
                  tippyConfig: { zIndex },
                  ...(mergeFields
                    ? { props: { mergeFields, listHeaders: false } }
                    : {}),
                }),
              },
            }),
          getCodeBlockExtension(codeBlockLanguage, disableCodeBlockEscape),
        ].filter(Boolean),
        content: initialValue,
        autofocus,
        editable: !disabled,
      };
    }, editorConfigDeps); // eslint-disable-line react-hooks/exhaustive-deps

    const editor = useTipTapEditor(editorConfig, editorConfigDeps);
    const editorRef = useRef(editor);
    editorRef.current = editor;

    if (editor) {
      // We're tracking the line height of the last visited paragraph so
      // it can be maintained when there are elements in-between paragraphs
      // (horizontal rule) that we don't want to cause a line height reset.
      const attributes = editor.getAttributes('paragraph');

      if (attributes.lineHeight) {
        editor.extensionStorage.lineHeight.currentLineHeight =
          attributes.lineHeight;
      }
    }

    useImperativeHandle(
      ref,
      () => ({
        editor,
        isEmpty: () => (editor ? editor.getText().length === 0 : true),
        containerRef,
      }),
      [editor, containerRef]
    );

    useClickAway(containerRef, () => {
      if (isLoadingAIContent) {
        setShowDismissModal(true);
      }
    });

    return (
      <EditorContext.Provider value={{ editor }}>
        {children}
        <EditorContainer
          ref={containerRef}
          fitToContent={fitToContent}
          floating={floating}
          showBorder={showBorder}
          enableResize={enableResize}
          initialHeight={initialHeight}
          minHeight={minHeight}
          defaultFontFamily={defaultFontFamily}
          defaultFontSize={defaultFontSize}
          hasLoadingState={enableAI}
          isLoading={isLoadingAIContent}
          codeBlockColors={CODE_BLOCK_LANGUAGES[codeBlockLanguage]?.colors}
        >
          <StyledEditorContent
            editor={editor}
            disabled={disabled}
            enableResize={enableResize}
            initialHeight={initialHeight}
            blurContent={isLoadingAIContent}
          />
          {bottomBarNode && (
            <BottomBarWrapper>{bottomBarNode}</BottomBarWrapper>
          )}
          {isLoadingAIContent && (
            <LoadingContainer className="wysiwyg-loading-container">
              <ClipLoader color={grayScale.dark} />
              <Button
                variant="light"
                color="blue"
                onClick={(ev) => {
                  ev.stopPropagation();
                  cancelRequest();
                }}
              >
                {t('Cancel AI Processing')}
              </Button>
            </LoadingContainer>
          )}
        </EditorContainer>
      </EditorContext.Provider>
    );
  }
);

WYSIWYG.displayName = 'WYSIWYG';
